import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts/'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import {
  Container,
  ChartContainer,
  SubContainerChart,
  RowContainer,
  SkillsContainer,
  ReportContainer,
  HeaderButtonsWrapper,
  WrapperInfo,
  Subtitle,
  InfoText,
  InfoUserContainer,
  ScoreSectionWrapper,
  ScoreWrapperInfo,
  CardInfoWithBorder,
  CompetenceResultWrapper,
  AdjustmentLevelText,
  HeaderInfoText,
  SeeResultsButtonWrapper,
  SubContainerChartText,
  ButtonContainer,
  DevelopmentButtonContainer,
  OrderedList,
  ListItem,
} from './styled'
import Radar from '../Graphics/Radar'
import { H2, H4 } from 'views/components/UI/Text'
import CheckboxLabels from 'views/components/UI/CheckboxLabels'
import { StrokedButton, BackButton } from 'views/components/UI/Buttons'
import CardDownloadResults from 'views/components/Cards/DownloadResults'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useNotification } from 'lib/context/notification.context'
import getSkillResult from 'services/results/skills-result'
import sendReportToUser from 'services/reports/sendReportResultToUserEvaluated/sendReportToUser'
import { IResultsSkills } from 'types'
import { useDispatch } from 'react-redux'
import { cleanPercentiles, setEvaluatedUserId, setTestId } from '../../screens/super-admin/percentiles/store'
import { fetchUpdateUserTestAction } from 'views/screens/Platform-administrator/constants/fetch_process'
import { platformAdminRoutes } from 'router/routes'
import { useTranslation } from 'react-i18next'
import { fetchFeedBack } from 'views/screens/Evaluated-User/services/fetch-development-plan-betesa'
import { deleteDevelopmentPlan } from 'views/screens/super-admin/services/delete-development-plan'
import getSkillsReport from 'services/pdfReports/getSkillsReport'
import { followUpApi } from 'services/results/follow-up'
import { adjustmentLevelColor } from 'utils/adjustmentLevelColor'
import RollBackIcon from 'assets/icons/RollBackIcon'
import { closeReportWindow, createPendingReportWindow, openReportInWindow } from 'utils/openReportInNewTab'
const MAX_COMPETENCES_SELECTED = 6
const MIN_COMPETENCES_SELECTED = 3

interface MeasurementOption {
  id: number
  test_id: number
  complete: boolean
  answer_date: string | null
  label: string
}

const ResultsSkills: React.FC = () => {
  const { t } = useTranslation()
  const location: any = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getError, getSuccess, getWarning } = useNotification()
  const { userName, testId, userId, companyId } = location.state || {}
  const requestedMeasurementId = Number(location.state?.userTestId || location.state?.user_test_id) || null
  const { token } = useSelector((state: any) => state.user)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingReport, setLoadingReport] = useState<boolean>(false)
  const [isSendingReport, setIsSendingReport] = useState<boolean>(false)
  const [resultData, setResultData] = useState<IResultsSkills | null>(null)
  const [selectedCompetences, setSelectedCompetences] = useState(new Set<string>())
  const [measurements, setMeasurements] = useState<MeasurementOption[]>([])
  const [selectedMeasurementId, setSelectedMeasurementId] = useState<number | null>(requestedMeasurementId)
  const [measurementsReady, setMeasurementsReady] = useState<boolean>(!location.state?.processId)
  const [measurementsError, setMeasurementsError] = useState<boolean>(false)
  const [resultsError, setResultsError] = useState<boolean>(false)
  const [disablePlanButton, setDisablePlanButton] = useState<boolean>(false)
  const [participation, setParticipation] = useState<boolean>(false)
  const { role } = useSelector((state: any) => state.auth)
  const [processId, setProcessId] = useState<number>(Number(location.state?.processId) || 0)
  const userTestId = Number(selectedMeasurementId || resultData?.user_result?.[0]?.user_test_id || 0)

  useEffect(() => {
    if (!processId || !userId || !testId) return
    let cancelled = false
    setMeasurementsReady(false)
    followUpApi(processId, `measurements?user_id=${userId}&category_id=2`)
      .then((rows: MeasurementOption[]) => {
        if (cancelled) return
        const completed = rows
          .filter((row) => Number(row.test_id) === Number(testId) && row.complete)
          .sort((a, b) =>
            String(b.answer_date || '').localeCompare(String(a.answer_date || '')) || b.id - a.id
          )
        setMeasurements(completed)
        setSelectedMeasurementId(
          requestedMeasurementId && completed.some((row) => row.id === requestedMeasurementId)
            ? requestedMeasurementId
            : completed[0]?.id || null
        )
        setMeasurementsError(false)
      })
      .catch(() => {
        if (cancelled) return
        setMeasurements([])
        setSelectedMeasurementId(null)
        setMeasurementsError(true)
        getError('No se pudo cargar el historial de 3D Skills.')
      })
      .finally(() => {
        if (!cancelled) setMeasurementsReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [processId, userId, testId, requestedMeasurementId, getError])
  const idealProfileData = useMemo(() => {
    if (!resultData) return []
    const idealProfileMap = new Map((resultData.ideal_profile || []).map((item: any) => [item.skill_name, item.weight]))
    const data = Array.from(selectedCompetences).map((competence) => idealProfileMap.get(competence) || 0)

    return data.every((value: any) => value === 0) ? undefined : data
  }, [resultData, selectedCompetences])

  useEffect(() => {
    if (!userTestId) return
    let cancelled = false
    setDisablePlanButton(false)
    fetchFeedBack(userTestId)
      .then((res: any) => {
        if (!cancelled && res.data.status === 'OK') {
          setDisablePlanButton(true)
        }
      })
      .catch(() => {
        if (!cancelled) setDisablePlanButton(false)
      })
    return () => {
      cancelled = true
    }
  }, [userTestId])

  useEffect(() => {
    if (!token || !userId || !testId || !measurementsReady) return
    if (processId && !selectedMeasurementId) {
      setResultData(null)
      return
    }
    let cancelled = false
    dispatch(cleanPercentiles())
    dispatch(setTestId(testId))
    dispatch(setEvaluatedUserId(userId))
    setIsLoading(true)
    setResultData(null)
    setResultsError(false)
    getSkillResult(token, userId, testId, selectedMeasurementId || undefined)
      .then((res: any) => {
        if (cancelled) return
        if (res.status === 'OK') {
          res.user_result.sort((a: any, b: any) => {
            if (b.overall_score === a.overall_score) {
              return a.skill_name.toLowerCase() > b.skill_name.toLowerCase() ? 1 : -1
            }
            return b.overall_score - a.overall_score
          })
          setResultData(res)
          setProcessId(Number(res.process_id) || 0)
          setSelectedCompetences(
            new Set(res.user_result.slice(0, MAX_COMPETENCES_SELECTED).map((item: any) => item.skill_name))
          )
        } else {
          getError(`Error obteniendo los resultados de 3D Skills: ${res.message || 'respuesta no disponible'}`)
          setResultData(null)
          setSelectedCompetences(new Set<string>())
          setResultsError(true)
        }
      })
      .catch(() => {
        if (cancelled) return
        getError('No se pudieron cargar los resultados de 3D Skills.')
        setResultData(null)
        setSelectedCompetences(new Set<string>())
        setResultsError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch, userId, testId, selectedMeasurementId, measurementsReady, processId])

  const handleSendReport = useCallback(async () => {
    if (isSendingReport) return
    try {
      setIsSendingReport(true)
      const response = await sendReportToUser({ userId, companyId, userTestId })
      if (response.status !== 'OK' && response.status !== 'success') {
        throw new Error(response.message || 'No se pudo enviar el informe')
      }
      setParticipation(true)
      await fetchUpdateUserTestAction(userTestId, true)
      getSuccess('Reporte enviado correctamente')
    } catch (error) {
      console.error('error', error)
      getError('Error al intentar enviar el reporte, comunícate con soporte.')
    } finally {
      setIsSendingReport(false)
    }
  }, [isSendingReport, userId, companyId, getSuccess, getError, userTestId])

  const handleCheckboxChange = useCallback(
    (competence: string) => {
      setSelectedCompetences((prevCompetences) => {
        const newCompetences = new Set(prevCompetences)
        if (newCompetences.has(competence)) {
          if (newCompetences.size <= MIN_COMPETENCES_SELECTED) {
            getError(t('global.notifications.addSkillsErrorMessage', { number: MIN_COMPETENCES_SELECTED }))
            return newCompetences
          }
          newCompetences.delete(competence)
        } else {
          if (newCompetences.size >= MAX_COMPETENCES_SELECTED) {
            getError(t('global.notifications.addSkillsErrorMessage', { number: MAX_COMPETENCES_SELECTED }))
          } else {
            newCompetences.add(competence)
          }
        }
        return newCompetences
      })
    },
    [getError, t]
  )

  const competencesLabels = useMemo(() => Array.from(selectedCompetences), [selectedCompetences])
  const competencesData = useMemo(() => {
    if (!resultData) return []
    const dataMap = new Map(resultData.user_result.map((item: any) => [item.skill_name, item.overall_score]))
    return competencesLabels.map((competence) => dataMap.get(competence) || 0)
  }, [resultData, competencesLabels])

  const handleDisabledClick = () => {
    getWarning('El usuario tiene configurada la descarga de reporte automático o ya se autorizó el acceso')
  }

  const handlePlanDisabledClick = () => {
    getWarning(t('global.notifications.planAlreadyCreated'))
  }

  const handleCreatePlan = () => {
    const body = {
      companyId,
      processId,
      userId,
      nameProcess: resultData?.process_name,
      testData: [
        {
          testId,
          typeTest: 'Competencias',
          typeLabel: 'skills',
          userTestId,
          domination: 'Interpretable',
        },
      ],
    }
    navigate(platformAdminRoutes.DevelopmentPlan, {
      state: {
        body,
        userName,
        flag: true,
      },
    })
  }

  const handleDeletePlan = async () => {
    const response = await deleteDevelopmentPlan(userTestId)
    if (response.status === 'OK') {
      getSuccess('Plan de desarrollo restaurado correctamente')
      setDisablePlanButton(false)
    } else {
      getError('Error al restaurar el plan de desarrollo')
    }
  }

  const downloadReport = async () => {
    const reportWindow = createPendingReportWindow()

    try {
      setLoadingReport(true)
      const response = await getSkillsReport(userTestId)

      const { url } = response

      await openReportInWindow(reportWindow, url)

      getSuccess(t('global.notifications.reportGenerated'))
    } catch (error) {
      closeReportWindow(reportWindow)
      console.error('Error al descargar el reporte de 3D Skills:', error)
      getError('No fue posible abrir el reporte de 3D Skills.')
    } finally {
      setLoadingReport(false)
    }
  }

  return (
    <MainLayout>
      {!isLoading && measurementsReady && resultData &&
      (!selectedMeasurementId || Number(resultData.user_result[0]?.user_test_id) === selectedMeasurementId) ? (
        <Container>
          <RowContainer justifyContent="space-between">
            <H2>
              {t('global.common.skillText')} - {userName}
            </H2>
            <HeaderButtonsWrapper>
              {measurements.length > 1 && (
                <label>
                  Medición consultada
                  <select
                    aria-label="Medición consultada"
                    value={selectedMeasurementId || ''}
                    onChange={(event) => setSelectedMeasurementId(Number(event.target.value))}
                  >
                    {measurements.map((measurement) => (
                      <option key={measurement.id} value={measurement.id}>
                        {measurement.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {role === 'platform_admin' || role === 'super_platform_admin' ? (
                <StrokedButton
                  disabled={disablePlanButton}
                  onClick={handleCreatePlan}
                  onDisabledClick={handlePlanDisabledClick}
                >
                  Crear plan de desarrollo
                </StrokedButton>
              ) : null}
              <StrokedButton
                onClick={handleSendReport}
                disabled={
                  resultData?.user_result[0].participant_report ||
                  participation ||
                  resultData?.user_result[0].access_report
                }
                onDisabledClick={handleDisabledClick}
              >
                Autorizar acceso a usuario
              </StrokedButton>
            </HeaderButtonsWrapper>
          </RowContainer>
          <RowContainer>
            <WrapperInfo>
              <H4>Datos de la Prueba</H4>
              <RowContainer>
                <Subtitle>Empresa:</Subtitle>
                <HeaderInfoText>{resultData?.company_name}</HeaderInfoText>

                <Subtitle>Nombre del Proceso:</Subtitle>
                <HeaderInfoText>{resultData?.process_name}</HeaderInfoText>

                <Subtitle>Fecha de realización:</Subtitle>
                <HeaderInfoText>{resultData?.result_date}</HeaderInfoText>
              </RowContainer>
            </WrapperInfo>
          </RowContainer>
          <InfoUserContainer>
            <ScoreSectionWrapper>
              <ScoreWrapperInfo>
                <CardInfoWithBorder>
                  <H4 margin="6px 0px 10px 0px">{t('global.common.skillText')} evaluadas</H4>
                  <OrderedList>
                    {resultData?.user_result.map((item: any, index: number) => (
                      <ListItem key={item.skill_id || index}>
                        <CompetenceResultWrapper>
                          <Subtitle>{item.skill_name}: </Subtitle>
                          <InfoText>{`${item.overall_score} `}</InfoText>
                          {item?.scale_name && <InfoText>({`${item.scale_name}`})</InfoText>}
                          <InfoText style={{ color: adjustmentLevelColor(item.competence_adjustment_level), fontWeight: 700 }}>
                            Ajuste con perfil ideal: {item.competence_adjustment_level || 'No disponible'}
                            {item.competence_adjustment_pct !== null && item.competence_adjustment_pct !== undefined
                              ? ` (${item.competence_adjustment_pct}%)`
                              : ''}
                          </InfoText>
                        </CompetenceResultWrapper>
                      </ListItem>
                    ))}
                  </OrderedList>
                  {resultData.historical_profile_available === false && (
                    <InfoText>El perfil ideal histórico no está disponible para esta medición.</InfoText>
                  )}
                  <RowContainer>
                    <Subtitle>Ajuste con el perfil ideal:</Subtitle>
                    {resultData.level_competence || resultData.level_score || resultData.level_score === 0 ? (
                      <AdjustmentLevelText>
                        {`${resultData.level_competence} - ${Math.min(resultData.level_score, 100)}%`}
                      </AdjustmentLevelText>
                    ) : null}
                  </RowContainer>
                  <RowContainer>
                    <Subtitle>Nivel:</Subtitle>
                    <AdjustmentLevelText>{`${resultData.level}`}</AdjustmentLevelText>
                  </RowContainer>
                  <RowContainer>
                    {role === 'platform_admin' || role === 'super_platform_admin' ? null : (
                      <SeeResultsButtonWrapper>
                        <StrokedButton type="button">
                          <Link to="/percentiles/" state={{ level: resultData.level, userTestId, userId, testId }}>
                            Ver resultados
                          </Link>
                        </StrokedButton>
                      </SeeResultsButtonWrapper>
                    )}
                  </RowContainer>
                </CardInfoWithBorder>
              </ScoreWrapperInfo>
              <ButtonContainer>
                <ReportContainer>
                  <CardDownloadResults
                    title={t('global.common.skillText')}
                    type="skills"
                    onClick={downloadReport}
                    user_id={userId}
                    test_id={testId}
                    user_test_id={userTestId}
                    processId={processId}
                    planToDownload={disablePlanButton}
                    userName={userName}
                    setLoadingReport={setLoadingReport}
                  />
                </ReportContainer>
                {disablePlanButton && (role === 'super_admin' || role === 'special_admin') && (
                  <DevelopmentButtonContainer>
                    <StrokedButton
                      onClick={handleDeletePlan}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px' }}
                    >
                      <RollBackIcon color="#2d749c" />
                      Restaurar plan de desarrollo
                    </StrokedButton>
                  </DevelopmentButtonContainer>
                )}
              </ButtonContainer>
            </ScoreSectionWrapper>
          </InfoUserContainer>
          <ChartContainer>
            <SubContainerChart>
              <Radar
                scoreContext="individual"
                userResultLabels={competencesLabels}
                userResultData={[]}
                userScoreResult={competencesData}
                idealProfileData={idealProfileData}
                labelResult="Usuario"
              />
            </SubContainerChart>
            <SubContainerChartText>
              <SkillsContainer>
                {resultData?.user_result.map((item: any, index: number) => {
                  return (
                    <CheckboxLabels
                      checked={selectedCompetences.has(item.skill_name)}
                      onChange={() => handleCheckboxChange(item.skill_name)}
                      key={index}
                      label={item.skill_name}
                    />
                  )
                })}
              </SkillsContainer>
            </SubContainerChartText>
          </ChartContainer>
        </Container>
      ) : (
        <>
          {!isLoading && measurementsReady && !isSendingReport ? (
            <H2 style={{ color: 'rgb(198, 1, 107)', marginTop: '60px', minHeight: '80%' }}>
              {measurementsError
                ? 'No se pudo cargar el historial de 3D Skills.'
                : resultsError
                ? 'No se pudieron cargar los resultados de esta medición 3D Skills.'
                : 'Este usuario no tiene mediciones 3D Skills completadas en este proceso.'}
            </H2>
          ) : (
            <ModalLoader isLoading={isLoading} />
          )}
        </>
      )}
      <BackButton onClick={() => navigate(-1)} />
      <ModalLoader isLoading={loadingReport} fontWeight={600} margin="20px 0" text="Estamos generando el archivo..." />
    </MainLayout>
  )
}

export default ResultsSkills
